"use client";
/* eslint-disable react-hooks/set-state-in-effect */

import { useEffect, useState } from "react";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import { landingContent } from "@/content/landing";
import { siteConfig } from "@/config/site";

const STORAGE_KEYS = {
  transcripta: "echoflow_cookie_consent_v1",
  scriptario: "scriptario_cookie_consent_v1",
  audio2text: "audio2text_cookie_consent_v1",
} as const;

const Banner = styled(motion.div)`
  position: fixed;
  right: 20px;
  left: auto;
  bottom: 20px;
  background: #fff;
  color: #1a1d1f;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(16, 24, 40, 0.1);
  padding: 21px 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 2000;
  max-width: 450px;
  min-width: 220px;
  flex-direction: row;

  @media (max-width: 480px) {
    left: 20px;
    right: 20px;
    bottom: 20px;
  }

  /* На низком экране баннер дорастал до кнопки «Выбрать файлы» в первом
     экране и перехватывал тап на себя: при 360x640 он занимал 541..620, а
     кнопка стоит на y=548. Зарезервировать место внизу страницы тут не
     помогает — баннер висит поверх, а не в потоке. Поэтому на таких экранах
     он ужимается: меньше отступы, кнопки остаются в строку. */
  @media (max-height: 700px) {
    padding: 10px 12px;
    gap: 8px;
  }
`;

const Text = styled.div`
  flex: 1;
  font-size: 14px;
  color: #333;
  line-height: 1.2;

  @media (max-width: 400px) {
    font-size: 12px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;

  @media (max-width: 400px) {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  /* Столбик кнопок удваивает высоту баннера. На низком экране это и есть
     разница между «не достаёт до кнопки» и «перекрывает её». */
  @media (max-height: 700px) {
    flex-direction: row;
    align-items: center;
  }
`;

const Action = styled.button<{ $variant?: "primary" | "secondary" }>`
  background: ${({ $variant = "primary" }) => $variant === "primary" ? "var(--brand-primary)" : "#fff"};
  color: ${({ $variant = "primary" }) => $variant === "primary" ? "#fff" : "rgba(0,0,0,0.72)"};
  border: ${({ $variant = "primary" }) => $variant === "primary" ? "none" : "1px solid rgba(0,0,0,0.12)"};
  padding: 8px 12px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

export default function CookieConsent() {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const cookie = landingContent.cookie;
  const storageKey = STORAGE_KEYS[siteConfig.siteId] ?? STORAGE_KEYS.transcripta;

  useEffect(() => {
    setMounted(true);

    try {
      const value = window.localStorage.getItem(storageKey);
      setVisible(!value);
    } catch {
      setVisible(true);
    }
  }, [storageKey]);

  const setConsent = (value: "accepted" | "rejected") => {
    try {
      window.localStorage.setItem(storageKey, value);
      window.dispatchEvent(new CustomEvent("cookie-consent", { detail: { value } }));
    } catch {}

    setVisible(false);
  };

  if (!mounted) {
    return null;
  }

  return (
    <AnimatePresence>
      {visible && (
        <Banner
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 24 }}
          transition={{ duration: 0.22 }}
        >
          <Text>
            {cookie.textLine1} <br />
            {cookie.textLine2}
          </Text>
          <Actions>
            {cookie.reject && (
              <Action $variant="secondary" onClick={() => setConsent("rejected")}>
                {cookie.reject}
              </Action>
            )}
            <Action onClick={() => setConsent("accepted")}>{cookie.accept}</Action>
          </Actions>
        </Banner>
      )}
    </AnimatePresence>
  );
}
